<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters } from 'dashboard/composables/store';
import WhatsappTemplatesAPI from 'dashboard/api/whatsappTemplates';
import Modal from 'dashboard/components/Modal.vue';
import ButtonV4 from 'next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close', 'created']);

const { t } = useI18n();
const getters = useStoreGetters();

const accountId = computed(() => getters.getCurrentAccountId.value);

const submitting = ref(false);
const templateForm = ref({
  name: '',
  category: 'utility',
  language: 'pt_BR',
  bodyText: '',
  headerText: '',
  footerText: '',
  buttons: [],
  headerExamples: [],
  bodyExamples: [],
});

const BUTTON_TYPES = [
  { value: 'QUICK_REPLY', label: 'QUICK_REPLY' },
  { value: 'PHONE_NUMBER', label: 'PHONE_NUMBER' },
  { value: 'URL', label: 'URL' },
];

const CATEGORIES = [
  { value: 'utility', label: 'UTILITY' },
  { value: 'marketing', label: 'MARKETING' },
  { value: 'authentication', label: 'AUTHENTICATION' },
];

const LANGUAGES = [
  { value: 'pt_BR', label: 'Português (Brasil)' },
  { value: 'en', label: 'English' },
  { value: 'en_US', label: 'English (US)' },
  { value: 'es', label: 'Español' },
  { value: 'es_ES', label: 'Español (España)' },
];

// Detect variables in text ({{1}}, {{2}}, etc.)
const extractVariables = text => {
  if (!text) return [];
  const regex = /\{\{(\d+)\}\}/g;
  const matches = [];
  let match;
  while ((match = regex.exec(text)) !== null) {
    matches.push(parseInt(match[1], 10));
  }
  return [...new Set(matches)].sort((a, b) => a - b);
};

// Watch for changes in header/body text and update examples array
const headerVariables = computed(() =>
  extractVariables(templateForm.value.headerText)
);
const bodyVariables = computed(() =>
  extractVariables(templateForm.value.bodyText)
);

// Auto-resize examples arrays when variables change
const syncExamplesWithVariables = (variables, examplesArray) => {
  const currentLength = examplesArray.length;
  const neededLength = variables.length;

  if (neededLength > currentLength) {
    // Add empty strings for new variables
    for (let i = currentLength; i < neededLength; i++) {
      examplesArray.push('');
    }
  } else if (neededLength < currentLength) {
    // Remove excess examples
    examplesArray.splice(neededLength);
  }
};

const addButton = () => {
  if (templateForm.value.buttons.length >= 3) {
    useAlert('Máximo de 3 botões permitido');
    return;
  }
  templateForm.value.buttons.push({
    type: 'QUICK_REPLY',
    text: '',
    url: '',
    phone_number: '',
  });
};

const removeButton = index => {
  templateForm.value.buttons.splice(index, 1);
};

// Format variable number as {{N}}
const formatVariable = num => `{{${num}}}`;

// Watch for variable changes and sync examples
watch(headerVariables, newVars => {
  syncExamplesWithVariables(newVars, templateForm.value.headerExamples);
});

watch(bodyVariables, newVars => {
  syncExamplesWithVariables(newVars, templateForm.value.bodyExamples);
});

const buildTemplateComponents = () => {
  const components = [];

  // Add header component if exists
  if (templateForm.value.headerText.trim()) {
    const headerComponent = {
      type: 'HEADER',
      format: 'TEXT',
      text: templateForm.value.headerText.trim(),
    };

    // Add header examples if there are variables
    if (headerVariables.value.length > 0) {
      const examples = templateForm.value.headerExamples.filter(ex =>
        ex.trim()
      );
      if (examples.length === headerVariables.value.length) {
        headerComponent.example = {
          header_text: [examples],
        };
      }
    }

    components.push(headerComponent);
  }

  // Add body component (required)
  const bodyComponent = {
    type: 'BODY',
    text: templateForm.value.bodyText.trim(),
  };

  // Add body examples if there are variables
  if (bodyVariables.value.length > 0) {
    const examples = templateForm.value.bodyExamples.filter(ex => ex.trim());
    if (examples.length === bodyVariables.value.length) {
      bodyComponent.example = {
        body_text: [examples],
      };
    }
  }

  components.push(bodyComponent);

  // Add footer component if exists
  if (templateForm.value.footerText.trim()) {
    components.push({
      type: 'FOOTER',
      text: templateForm.value.footerText.trim(),
    });
  }

  // Add buttons component if exists
  if (templateForm.value.buttons.length > 0) {
    const validButtons = templateForm.value.buttons
      .filter(btn => btn.text.trim())
      .map(btn => {
        const button = {
          type: btn.type,
          text: btn.text.trim(),
        };

        if (btn.type === 'URL' && btn.url.trim()) {
          button.url = btn.url.trim();
        } else if (btn.type === 'PHONE_NUMBER' && btn.phone_number.trim()) {
          button.phone_number = btn.phone_number.trim();
        }

        return button;
      });

    if (validButtons.length > 0) {
      components.push({
        type: 'BUTTONS',
        buttons: validButtons,
      });
    }
  }

  return components;
};

const createTemplate = async () => {
  if (!templateForm.value.name || !templateForm.value.bodyText) {
    useAlert(t('INBOX_MGMT.TEMPLATES.VALIDATION.REQUIRED_FIELDS'));
    return;
  }

  // Validate header examples if there are header variables
  if (headerVariables.value.length > 0) {
    const filledExamples = templateForm.value.headerExamples.filter(ex =>
      ex.trim()
    );
    if (filledExamples.length !== headerVariables.value.length) {
      useAlert(
        t('INBOX_MGMT.TEMPLATES.VALIDATION.HEADER_EXAMPLES_REQUIRED', {
          count: headerVariables.value.length,
        })
      );
      return;
    }
  }

  // Validate body examples if there are body variables
  if (bodyVariables.value.length > 0) {
    const filledExamples = templateForm.value.bodyExamples.filter(ex =>
      ex.trim()
    );
    if (filledExamples.length !== bodyVariables.value.length) {
      useAlert(
        t('INBOX_MGMT.TEMPLATES.VALIDATION.BODY_EXAMPLES_REQUIRED', {
          count: bodyVariables.value.length,
        })
      );
      return;
    }
  }

  submitting.value = true;
  try {
    const templateData = {
      name: templateForm.value.name,
      category: templateForm.value.category,
      language: templateForm.value.language,
      components: buildTemplateComponents(),
    };

    await WhatsappTemplatesAPI.createTemplate(
      accountId.value,
      props.inbox.id,
      templateData
    );

    useAlert(t('INBOX_MGMT.TEMPLATES.API.CREATE_SUCCESS'));
    emit('created');
  } catch (error) {
    const errorMessage =
      error.response?.data?.error || t('INBOX_MGMT.TEMPLATES.API.CREATE_ERROR');
    useAlert(errorMessage);
  } finally {
    submitting.value = false;
  }
};

const handleClose = () => {
  emit('close');
};
</script>

<template>
  <Modal :show="true" :on-close="handleClose">
    <div class="flex flex-col w-full h-auto max-w-2xl">
      <!-- Header -->
      <div
        class="flex justify-between items-center p-6 border-b border-n-weak bg-n-solid-1"
      >
        <div class="flex gap-3 items-center">
          <div
            class="flex justify-center items-center w-10 h-10 rounded-lg bg-n-blue-2"
          >
            <Icon
              icon="i-lucide-message-square-plus"
              class="w-5 h-5 text-n-blue-11"
            />
          </div>
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{ t('INBOX_MGMT.TEMPLATES.CREATE_MODAL.TITLE') }}
          </h2>
        </div>
        <button
          class="p-1.5 rounded-lg hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12 transition-colors"
          @click="handleClose"
        >
          <Icon icon="i-lucide-x" class="w-5 h-5" />
        </button>
      </div>

      <!-- Form -->
      <div
        class="flex-1 p-6 space-y-5 overflow-y-auto max-h-[70vh] bg-n-solid-2"
      >
        <!-- Template Name -->
        <div>
          <label
            class="flex gap-1 items-center mb-2 text-sm font-medium text-n-slate-12"
          >
            <Icon
              icon="i-lucide-file-signature"
              class="w-4 h-4 text-n-slate-11"
            />
            {{ t('INBOX_MGMT.TEMPLATES.FORM.NAME_LABEL') }}
            <span class="text-n-ruby-11">*</span>
          </label>
          <input
            v-model="templateForm.name"
            type="text"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 placeholder:text-n-slate-10 focus:border-n-blue-8 focus:ring-2 focus:ring-n-blue-3 outline-none transition-all font-mono"
            :placeholder="t('INBOX_MGMT.TEMPLATES.FORM.NAME_PLACEHOLDER')"
          />
          <p class="flex gap-1 items-start mt-1.5 text-xs text-n-slate-11">
            <Icon icon="i-lucide-info" class="flex-shrink-0 w-3 h-3 mt-0.5" />
            <span>{{ t('INBOX_MGMT.TEMPLATES.FORM.NAME_HINT') }}</span>
          </p>
        </div>

        <!-- Category -->
        <div>
          <label
            class="flex gap-1 items-center mb-2 text-sm font-medium text-n-slate-12"
          >
            <Icon icon="i-lucide-tag" class="w-4 h-4 text-n-slate-11" />
            {{ t('INBOX_MGMT.TEMPLATES.FORM.CATEGORY_LABEL') }}
            <span class="text-n-ruby-11">*</span>
          </label>
          <select
            v-model="templateForm.category"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:border-n-blue-8 focus:ring-2 focus:ring-n-blue-3 outline-none transition-all"
          >
            <option
              v-for="cat in CATEGORIES"
              :key="cat.value"
              :value="cat.value"
            >
              {{ t(`INBOX_MGMT.TEMPLATES.CATEGORIES.${cat.label}`) }}
            </option>
          </select>
        </div>

        <!-- Language -->
        <div>
          <label
            class="flex gap-1 items-center mb-2 text-sm font-medium text-n-slate-12"
          >
            <Icon icon="i-lucide-globe" class="w-4 h-4 text-n-slate-11" />
            {{ t('INBOX_MGMT.TEMPLATES.FORM.LANGUAGE_LABEL') }}
            <span class="text-n-ruby-11">*</span>
          </label>
          <select
            v-model="templateForm.language"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:border-n-blue-8 focus:ring-2 focus:ring-n-blue-3 outline-none transition-all"
          >
            <option
              v-for="lang in LANGUAGES"
              :key="lang.value"
              :value="lang.value"
            >
              {{ lang.label }}
            </option>
          </select>
        </div>

        <!-- Header Text -->
        <div>
          <label
            class="flex gap-1 items-center mb-2 text-sm font-medium text-n-slate-12"
          >
            <Icon icon="i-lucide-heading" class="w-4 h-4 text-n-slate-11" />
            {{ t('INBOX_MGMT.TEMPLATES.FORM.HEADER_LABEL') }}
          </label>
          <input
            v-model="templateForm.headerText"
            type="text"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 placeholder:text-n-slate-10 focus:border-n-blue-8 focus:ring-2 focus:ring-n-blue-3 outline-none transition-all"
            :placeholder="t('INBOX_MGMT.TEMPLATES.FORM.HEADER_PLACEHOLDER')"
            maxlength="60"
          />
          <p class="flex gap-1 items-start mt-1.5 text-xs text-n-slate-11">
            <Icon icon="i-lucide-info" class="flex-shrink-0 w-3 h-3 mt-0.5" />
            <span>{{ t('INBOX_MGMT.TEMPLATES.FORM.HEADER_HINT') }}</span>
          </p>

          <!-- Header Variable Examples -->
          <div
            v-if="headerVariables.length > 0"
            class="mt-3 p-3 rounded-lg border border-n-blue-6 bg-n-blue-2"
          >
            <div class="flex gap-2 items-center mb-2">
              <Icon icon="i-lucide-lightbulb" class="w-4 h-4 text-n-blue-11" />
              <span class="text-sm font-medium text-n-blue-12">
                {{ t('INBOX_MGMT.TEMPLATES.FORM.HEADER_EXAMPLES_LABEL') }}
              </span>
            </div>
            <div class="space-y-2">
              <div
                v-for="(varNum, index) in headerVariables"
                :key="`header-var-${varNum}`"
                class="flex gap-2 items-center"
              >
                <span
                  class="flex-shrink-0 px-2 py-1 text-xs font-mono rounded bg-n-blue-3 text-n-blue-11"
                  v-text="formatVariable(varNum)"
                />
                <input
                  v-model="templateForm.headerExamples[index]"
                  type="text"
                  class="flex-1 px-2 py-1 text-sm rounded border border-n-blue-6 bg-n-solid-1 text-n-slate-12 placeholder:text-n-slate-10 focus:border-n-blue-8 focus:ring-1 focus:ring-n-blue-3 outline-none"
                  :placeholder="
                    t('INBOX_MGMT.TEMPLATES.FORM.EXAMPLE_PLACEHOLDER', {
                      num: varNum,
                    })
                  "
                />
              </div>
            </div>
            <p class="flex gap-1 items-start mt-2 text-xs text-n-blue-11">
              <Icon icon="i-lucide-info" class="flex-shrink-0 w-3 h-3 mt-0.5" />
              <span>{{ t('INBOX_MGMT.TEMPLATES.FORM.EXAMPLES_HINT') }}</span>
            </p>
          </div>
        </div>

        <!-- Body Text -->
        <div>
          <label
            class="flex gap-1 items-center mb-2 text-sm font-medium text-n-slate-12"
          >
            <Icon
              icon="i-lucide-message-square-text"
              class="w-4 h-4 text-n-slate-11"
            />
            {{ t('INBOX_MGMT.TEMPLATES.FORM.BODY_LABEL') }}
            <span class="text-n-ruby-11">*</span>
          </label>
          <textarea
            v-model="templateForm.bodyText"
            rows="6"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 placeholder:text-n-slate-10 focus:border-n-blue-8 focus:ring-2 focus:ring-n-blue-3 outline-none transition-all resize-none"
            :placeholder="t('INBOX_MGMT.TEMPLATES.FORM.BODY_PLACEHOLDER')"
            maxlength="1024"
          />
          <p class="flex gap-1 items-start mt-1.5 text-xs text-n-slate-11">
            <Icon icon="i-lucide-info" class="flex-shrink-0 w-3 h-3 mt-0.5" />
            <span>{{ t('INBOX_MGMT.TEMPLATES.FORM.BODY_HINT') }}</span>
          </p>

          <!-- Body Variable Examples -->
          <div
            v-if="bodyVariables.length > 0"
            class="mt-3 p-3 rounded-lg border border-n-blue-6 bg-n-blue-2"
          >
            <div class="flex gap-2 items-center mb-2">
              <Icon icon="i-lucide-lightbulb" class="w-4 h-4 text-n-blue-11" />
              <span class="text-sm font-medium text-n-blue-12">
                {{ t('INBOX_MGMT.TEMPLATES.FORM.BODY_EXAMPLES_LABEL') }}
              </span>
            </div>
            <div class="space-y-2">
              <div
                v-for="(varNum, index) in bodyVariables"
                :key="`body-var-${varNum}`"
                class="flex gap-2 items-center"
              >
                <span
                  class="flex-shrink-0 px-2 py-1 text-xs font-mono rounded bg-n-blue-3 text-n-blue-11"
                  v-text="formatVariable(varNum)"
                />
                <input
                  v-model="templateForm.bodyExamples[index]"
                  type="text"
                  class="flex-1 px-2 py-1 text-sm rounded border border-n-blue-6 bg-n-solid-1 text-n-slate-12 placeholder:text-n-slate-10 focus:border-n-blue-8 focus:ring-1 focus:ring-n-blue-3 outline-none"
                  :placeholder="
                    t('INBOX_MGMT.TEMPLATES.FORM.EXAMPLE_PLACEHOLDER', {
                      num: varNum,
                    })
                  "
                />
              </div>
            </div>
            <p class="flex gap-1 items-start mt-2 text-xs text-n-blue-11">
              <Icon icon="i-lucide-info" class="flex-shrink-0 w-3 h-3 mt-0.5" />
              <span>{{ t('INBOX_MGMT.TEMPLATES.FORM.EXAMPLES_HINT') }}</span>
            </p>
          </div>
        </div>

        <!-- Footer Text -->
        <div>
          <label
            class="flex gap-1 items-center mb-2 text-sm font-medium text-n-slate-12"
          >
            <Icon icon="i-lucide-text" class="w-4 h-4 text-n-slate-11" />
            {{ t('INBOX_MGMT.TEMPLATES.FORM.FOOTER_LABEL') }}
          </label>
          <input
            v-model="templateForm.footerText"
            type="text"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 placeholder:text-n-slate-10 focus:border-n-blue-8 focus:ring-2 focus:ring-n-blue-3 outline-none transition-all"
            :placeholder="t('INBOX_MGMT.TEMPLATES.FORM.FOOTER_PLACEHOLDER')"
            maxlength="60"
          />
          <p class="flex gap-1 items-start mt-1.5 text-xs text-n-slate-11">
            <Icon icon="i-lucide-info" class="flex-shrink-0 w-3 h-3 mt-0.5" />
            <span>{{ t('INBOX_MGMT.TEMPLATES.FORM.FOOTER_HINT') }}</span>
          </p>
        </div>

        <!-- Buttons Section -->
        <div>
          <div class="flex gap-2 items-center justify-between mb-3">
            <label
              class="flex gap-1 items-center text-sm font-medium text-n-slate-12"
            >
              <Icon
                icon="i-lucide-mouse-pointer-click"
                class="w-4 h-4 text-n-slate-11"
              />
              Botões (Opcional)
            </label>
            <ButtonV4
              v-if="templateForm.buttons.length < 3"
              xs
              ghost
              blue
              @click="addButton"
            >
              <Icon icon="i-lucide-plus" class="w-3.5 h-3.5 mr-1" />
              Adicionar Botão
            </ButtonV4>
          </div>

          <div v-if="templateForm.buttons.length > 0" class="space-y-3">
            <div
              v-for="(button, index) in templateForm.buttons"
              :key="index"
              class="p-3 rounded-lg border border-n-weak bg-n-solid-1"
            >
              <div class="flex gap-2 items-start mb-2">
                <span class="text-xs font-semibold text-n-slate-11">
                  Botão {{ index + 1 }}
                </span>
                <button
                  class="ml-auto p-1 rounded hover:bg-n-ruby-2 text-n-slate-11 hover:text-n-ruby-11"
                  @click="removeButton(index)"
                >
                  <Icon icon="i-lucide-x" class="w-3.5 h-3.5" />
                </button>
              </div>

              <div class="space-y-2">
                <!-- Button Type -->
                <select
                  v-model="button.type"
                  class="w-full px-2 py-1.5 text-xs rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12"
                >
                  <option
                    v-for="btnType in BUTTON_TYPES"
                    :key="btnType.value"
                    :value="btnType.value"
                  >
                    {{
                      t(`INBOX_MGMT.TEMPLATES.BUTTON_TYPES.${btnType.label}`)
                    }}
                  </option>
                </select>

                <!-- Button Text -->
                <input
                  v-model="button.text"
                  type="text"
                  class="w-full px-2 py-1.5 text-xs rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12"
                  placeholder="Texto do botão"
                  maxlength="20"
                />

                <!-- URL Field -->
                <input
                  v-if="button.type === 'URL'"
                  v-model="button.url"
                  type="url"
                  class="w-full px-2 py-1.5 text-xs rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12"
                  placeholder="https://exemplo.com"
                />

                <!-- Phone Number Field -->
                <input
                  v-if="button.type === 'PHONE_NUMBER'"
                  v-model="button.phone_number"
                  type="tel"
                  class="w-full px-2 py-1.5 text-xs rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12"
                  placeholder="+5511999999999"
                />
              </div>
            </div>
          </div>

          <p
            v-else
            class="text-xs text-n-slate-11 text-center py-3 border border-dashed border-n-weak rounded-lg"
          >
            Nenhum botão adicionado. Clique em "Adicionar Botão" para criar.
          </p>
        </div>

        <!-- Info Box -->
        <div class="p-4 rounded-lg bg-n-blue-2 border border-n-blue-7">
          <div class="flex gap-3">
            <div
              class="flex-shrink-0 flex justify-center items-center w-8 h-8 rounded-full bg-n-blue-3"
            >
              <Icon icon="i-lucide-lightbulb" class="w-4 h-4 text-n-blue-11" />
            </div>
            <div class="text-xs text-n-blue-11">
              <p class="font-semibold mb-1.5 text-sm text-n-blue-12">
                {{ t('INBOX_MGMT.TEMPLATES.FORM.INFO_TITLE') }}
              </p>
              <p class="leading-relaxed">
                {{ t('INBOX_MGMT.TEMPLATES.FORM.INFO_MESSAGE') }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex gap-3 justify-end items-center p-6 border-t border-n-weak bg-n-solid-1"
      >
        <ButtonV4
          ghost
          slate
          :disabled="submitting"
          class="px-4"
          @click="handleClose"
        >
          {{ t('INBOX_MGMT.TEMPLATES.CREATE_MODAL.CANCEL') }}
        </ButtonV4>
        <ButtonV4
          solid
          blue
          :is-loading="submitting"
          :disabled="submitting"
          class="px-4"
          @click="createTemplate"
        >
          <Icon
            v-if="!submitting"
            icon="i-lucide-send"
            class="w-4 h-4 mr-1.5"
          />
          {{ t('INBOX_MGMT.TEMPLATES.CREATE_MODAL.SUBMIT') }}
        </ButtonV4>
      </div>
    </div>
  </Modal>
</template>
